import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import {
  PROBLEM_CODES,
  ROLE_LABELS,
  USER_STATUS_LABELS,
  api,
  errorMessage,
  problemCode,
  useApiMutation,
  useApiQuery,
} from "../../api";
import type { Invited, Role, User, UsersResponse, Uuid } from "../../api";
import { Badge, Button, Card, ConfirmDialog, EmptyState, Field, RoleBadge, Table, useToast } from "../../components";
import type { TableColumn } from "../../components";
import { formatDate } from "../../format";
import { AsyncBoundary } from "../../shell";
import styles from "./EquipeMembres.module.css";

/**
 * Les appels d'un périmètre. Deux périmètres, une seule interface : les
 * membres d'une entreprise et les comptes de l'ESN se gèrent avec les mêmes
 * gestes, seules les routes changent.
 */
export type MembresApi = {
  list: (opts: { signal: AbortSignal }) => Promise<UsersResponse>;
  invite: (email: string, role: Role) => Promise<Invited>;
  resend: (userId: Uuid) => Promise<Invited>;
  changeRole: (userId: Uuid, role: Role) => Promise<User>;
  remove: (userId: Uuid) => Promise<void>;
};

/** Les appels pour l'entreprise donnée. */
export function membresEntreprise(companyId: Uuid): MembresApi {
  return {
    list: (o) => api.listCompanyMembers(companyId, o),
    invite: (email, role) => api.inviteCompanyMember(companyId, { email, role }),
    resend: (userId) => api.resendInvitation(companyId, userId),
    changeRole: (userId, role) => api.changeMemberRole(companyId, userId, { role }),
    remove: (userId) => api.removeMember(companyId, userId),
  };
}

/** Les appels pour l'équipe de l'ESN. */
export function membresEsn(): MembresApi {
  return {
    list: (o) => api.listEsnUsers(o),
    invite: (email, role) => api.inviteEsnUser({ email, role }),
    resend: (userId) => api.resendEsnInvitation(userId),
    changeRole: (userId, role) => api.changeEsnUserRole(userId, { role }),
    remove: (userId) => api.removeEsnUser(userId),
  };
}

export type EquipeMembresProps = {
  /** Les appels du périmètre. */
  membres: MembresApi;
  /** Les deux rôles attribuables dans ce périmètre, l'administrateur en premier. */
  roles: readonly [admin: Role, member: Role];
  /** Le compte de la session : pas d'action sur sa propre ligne. */
  selfUserId: Uuid;
  /** Une clé qui change quand le périmètre change, pour relire. */
  scopeKey: string;
};

type Confirmation =
  | { type: "retirer"; user: User }
  | { type: "retrograder"; user: User };

/**
 * L'équipe d'un périmètre : ses membres actifs, ses invitations en attente,
 * ses comptes retirés, et les gestes dessus. Les invités SONT les invitations
 * en attente — il n'y a pas de seconde liste à tenir.
 *
 * Les gestes irréversibles d'un clic (retirer, rétrograder) passent par une
 * confirmation ; le dernier administrateur est protégé par le serveur, et
 * l'écran explique le refus.
 */
export function EquipeMembres({ membres, roles, selfUserId, scopeKey }: EquipeMembresProps) {
  const { notify, notifyError } = useToast();
  const liste = useApiQuery((o) => membres.list(o), [scopeKey]);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(roles[1]);
  const [erreurEmail, setErreurEmail] = useState<string | null>(null);

  const invitation = useApiMutation((e: string, r: Role) => membres.invite(e, r));
  const renvoi = useApiMutation((id: Uuid) => membres.resend(id));
  const changement = useApiMutation((id: Uuid, r: Role) => membres.changeRole(id, r));
  const retrait = useApiMutation((id: Uuid) => membres.remove(id));

  const [adminRole, memberRole] = roles;

  async function inviter(e: FormEvent) {
    e.preventDefault();
    setErreurEmail(null);
    if (email.trim() === "") {
      setErreurEmail("Indiquez une adresse.");
      return;
    }
    const inv = await invitation.run(email.trim(), role);
    if (inv === null) {
      return;
    }
    setEmail("");
    liste.reload();
    notify({
      title: inv.mail_sent ? "Invitation envoyée" : "Invitation créée, courriel non parti",
      description: inv.mail_sent
        ? `${inv.user.email} a reçu un lien valable jusqu'au ${formatDate(inv.expires_at)}.`
        : "Le courriel n'a pas pu partir. Utilisez « Renvoyer » quand le service de courriel sera rétabli.",
      tone: inv.mail_sent ? "success" : "warning",
    });
  }

  // L'erreur d'invitation se lit au rendu, pas après l'await (fermeture périmée).
  const codeInvitation = problemCode(invitation.error);
  const messageInvitation =
    codeInvitation === PROBLEM_CODES.emailTaken
      ? "Un compte porte déjà cette adresse."
      : codeInvitation === PROBLEM_CODES.invalidEmail
        ? "Cette adresse n'a pas une forme valide."
        : invitation.error !== null
          ? errorMessage(invitation.error)
          : null;

  async function renvoyer(u: User) {
    const inv = await renvoi.run(u.id);
    if (inv === null) {
      notifyError(renvoi.error, "Renvoi impossible");
      return;
    }
    notify({
      title: inv.mail_sent ? "Invitation renvoyée" : "Nouveau lien créé, courriel non parti",
      tone: inv.mail_sent ? "success" : "warning",
    });
  }

  async function changerRole(u: User, nouveau: Role) {
    const maj = await changement.run(u.id, nouveau);
    if (maj === null) {
      return;
    }
    setConfirmation(null);
    liste.reload();
    notify({ title: `${maj.email} est maintenant ${ROLE_LABELS[maj.role].toLowerCase()}`, tone: "success" });
  }

  async function retirer(u: User) {
    const ok = await retrait.run(u.id);
    if (ok === null) {
      return;
    }
    setConfirmation(null);
    liste.reload();
    notify({ title: `${u.email} a été retiré(e)`, tone: "neutral" });
  }

  // Refus du serveur sur un geste : expliqué dans la boîte de confirmation,
  // là où l'utilisateur regarde.
  const erreurGeste = confirmation?.type === "retirer" ? retrait.error : changement.error;
  const messageGeste =
    problemCode(erreurGeste) === PROBLEM_CODES.lastAdmin
      ? "Impossible : il faut au moins un administrateur. Nommez-en un autre d'abord."
      : problemCode(erreurGeste) === PROBLEM_CODES.selfTarget
        ? "Vous ne pouvez pas faire ce geste sur votre propre compte."
        : erreurGeste !== null
          ? errorMessage(erreurGeste)
          : null;

  const colonnes = useMemo<TableColumn<User>[]>(
    () => [
      {
        key: "email",
        header: "Adresse",
        render: (u) => (
          <span className={styles.email}>
            {u.email}
            {u.id === selfUserId && <span className={styles.vous}> (vous)</span>}
          </span>
        ),
      },
      { key: "role", header: "Rôle", render: (u) => <RoleBadge role={u.role} size="sm" /> },
      {
        key: "statut",
        header: "Statut",
        render: (u) => (
          <Badge
            size="sm"
            dot
            tone={u.status === "active" ? "success" : u.status === "invited" ? "warning" : "neutral"}
          >
            {USER_STATUS_LABELS[u.status]}
          </Badge>
        ),
      },
      { key: "depuis", header: "Depuis", render: (u) => formatDate(u.created_at) },
      {
        key: "actions",
        header: <span className="lde-visually-hidden">Actions</span>,
        align: "right",
        render: (u) => {
          if (u.id === selfUserId || u.status === "disabled") {
            return null;
          }
          return (
            <span className={styles.actions}>
              {u.status === "invited" && (
                <Button size="sm" variant="ghost" onClick={() => void renvoyer(u)} loading={renvoi.pending}>
                  Renvoyer l'invitation
                </Button>
              )}
              {u.status === "active" && u.role === memberRole && (
                <Button size="sm" variant="ghost" onClick={() => void changerRole(u, adminRole)}>
                  Passer {ROLE_LABELS[adminRole].toLowerCase()}
                </Button>
              )}
              {u.status === "active" && u.role === adminRole && (
                <Button size="sm" variant="ghost" onClick={() => setConfirmation({ type: "retrograder", user: u })}>
                  Passer {ROLE_LABELS[memberRole].toLowerCase()}
                </Button>
              )}
              <Button size="sm" variant="danger" onClick={() => setConfirmation({ type: "retirer", user: u })}>
                Retirer
              </Button>
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- les gestes lisent les mutations par fermeture ; seules les données d'identité comptent.
    [selfUserId, adminRole, memberRole, renvoi.pending],
  );

  return (
    <div className={styles.equipe}>
      <Card title="Inviter" subtitle="La personne reçoit un lien pour choisir son mot de passe. Il est valable trois jours.">
        <form className={styles.formulaire} onSubmit={(e) => void inviter(e)} noValidate>
          <Field label="Adresse e-mail" error={erreurEmail ?? messageInvitation ?? undefined} required className={styles.champEmail}>
            {(commande) => (
              <input
                {...commande}
                type="email"
                inputMode="email"
                spellCheck={false}
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={invitation.pending}
              />
            )}
          </Field>
          <Field label="Rôle" required>
            {(commande) => (
              <select
                {...commande}
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                disabled={invitation.pending}
              >
                <option value={memberRole}>{ROLE_LABELS[memberRole]}</option>
                <option value={adminRole}>{ROLE_LABELS[adminRole]}</option>
              </select>
            )}
          </Field>
          <Button type="submit" variant="primary" loading={invitation.pending}>
            Inviter
          </Button>
        </form>
      </Card>

      <AsyncBoundary
        state={liste}
        errorTitle="Équipe illisible"
        skeleton={<Table columns={colonnes} rows={[]} rowKey={(u) => u.id} caption="Membres" loading animateRows={false} />}
      >
        {(donnees) => (
          <Card padding="none">
            <Table
              columns={colonnes}
              rows={donnees.users}
              rowKey={(u) => u.id}
              caption="Membres du périmètre, invitations en attente comprises"
              animateRows={false}
              empty={<EmptyState compact title="Personne pour l'instant" description="Invitez un premier membre ci-dessus." />}
            />
          </Card>
        )}
      </AsyncBoundary>

      <ConfirmDialog
        open={confirmation !== null}
        title={confirmation?.type === "retirer" ? "Retirer ce compte ?" : "Rétrograder ce compte ?"}
        description={
          <>
            {confirmation?.type === "retirer"
              ? `${confirmation.user.email} ne pourra plus se connecter. Ses sessions ouvertes seront fermées. Le compte reste dans l'historique.`
              : confirmation
                ? `${confirmation.user.email} perdra la gestion de l'équipe et de la marque.`
                : ""}
            {messageGeste !== null && (
              <span className={styles.refus} role="alert">
                {" "}
                {messageGeste}
              </span>
            )}
          </>
        }
        confirmLabel={confirmation?.type === "retirer" ? "Retirer" : "Rétrograder"}
        tone="danger"
        pending={retrait.pending || changement.pending}
        onConfirm={() => {
          if (confirmation?.type === "retirer") void retirer(confirmation.user);
          if (confirmation?.type === "retrograder") void changerRole(confirmation.user, memberRole);
        }}
        onCancel={() => {
          setConfirmation(null);
          retrait.reset();
          changement.reset();
        }}
      />
    </div>
  );
}
